import { useEffect, useState, useMemo } from "react";
import { Lottie } from "lottie-react";
import { lottieUrlForEmoji } from "./animatedEmojiMap";

// In-memory cache so the same emoji Lottie JSON is only fetched once per session.
const cache = new Map(); // emoji -> animationData | "pending" | "missing"

/** Renders a single emoji as an animated Lottie if it's in the curated map,
 *  otherwise falls back to the native glyph (still animated by parent CSS).
 *  Size is a pixel value applied to both width and height.
 */
export default function AnimatedEmoji({ emoji, size = 72 }) {
  const url = useMemo(() => lottieUrlForEmoji(emoji), [emoji]);
  const [data, setData] = useState(() => {
    const cached = cache.get(emoji);
    return cached && cached !== "pending" && cached !== "missing" ? cached : null;
  });

  useEffect(() => {
    if (!url) return;
    const cached = cache.get(emoji);
    if (cached && cached !== "pending" && cached !== "missing") {
      setData(cached);
      return;
    }
    if (cached === "missing") return;
    if (cached === "pending") return;
    cache.set(emoji, "pending");
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("fetch failed"))))
      .then((json) => {
        if (cancelled) return;
        cache.set(emoji, json);
        setData(json);
      })
      .catch(() => {
        cache.set(emoji, "missing");
      });
    return () => { cancelled = true; };
  }, [emoji, url]);

  // No animation available → fall back to the native glyph (still bouncy via CSS).
  if (!url) {
    return <span className="animated-emoji-fallback" style={{ fontSize: size * 0.9, lineHeight: 1 }}>{emoji}</span>;
  }

  if (!data) {
    // Show the native glyph while the Lottie loads so nothing jumps around.
    return (
      <span
        className="animated-emoji-fallback"
        style={{ fontSize: size * 0.9, lineHeight: 1, display: "inline-block", width: size, height: size, textAlign: "center" }}
      >
        {emoji}
      </span>
    );
  }

  return (
    <span
      className="animated-emoji"
      style={{ display: "inline-block", width: size, height: size, verticalAlign: "middle" }}
    >
      <Lottie
        animationData={data}
        loop
        autoplay
        rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
        style={{ width: "100%", height: "100%" }}
      />
    </span>
  );
}
